import React from 'react'
import dynamic from 'next/dynamic'

import type { DrawerKey, DrawerPayloads } from './drawer.type'

type DrawerConfig<TPayload> = {
  // The provider will inject hideDrawer into payload before calling render
  render: (payload: TPayload & { hideDrawer: () => void }) => React.ReactNode
}

type DrawerConfigMap = {
  [K in DrawerKey]: DrawerConfig<DrawerPayloads[K]>
}

const DrawerSkeleton = () => (
  <div className="animate-pulse p-4">
    <div className="mb-3 h-5 w-1/3 rounded bg-gray-200" />
    <div className="space-y-3">
      {[0, 1, 2].map((key) => (
        <div key={key} className="h-10 rounded bg-gray-100" />
      ))}
    </div>
  </div>
)

function createLazyDrawer<TProps>(
  loader: () => Promise<{ default: React.ComponentType<TProps> }>,
) {
  const Lazy = dynamic(loader, {
    ssr: false,
    loading: () => <DrawerSkeleton />,
  })
  return { Lazy }
}

const ProductsActivitySheet = createLazyDrawer(() =>
  import('@/components/organisms/ProductsActivitySheet/productsActivitySheet.organism').then(
    (m) => ({ default: m.ProductsActivitySheet }),
  ),
)

const ProductHistoryDrawer = createLazyDrawer(() =>
  import('@/components/organisms/ProductHistoryDrawer').then((m) => ({
    default: m.ProductHistoryDrawer,
  })),
)

const ReviewsDrawer = createLazyDrawer(() =>
  import('@/components/organisms/Reviews').then((m) => ({
    default: m.ReviewsDrawer,
  })),
)

const InventoryProductFormDrawer = createLazyDrawer(() =>
  import('@/components/organisms/InventoryProductFormDrawer').then((m) => ({
    default: m.InventoryProductFormDrawer,
  })),
)

export const drawerRegistry: DrawerConfigMap = {
  productsActivity: {
    render: (props) => <ProductsActivitySheet.Lazy {...props} />,
  },
  productHistory: {
    render: (props) => <ProductHistoryDrawer.Lazy {...props} />,
  },
  reviews: {
    render: () => <ReviewsDrawer.Lazy />,
  },
  inventoryProductForm: {
    render: (props) => <InventoryProductFormDrawer.Lazy {...props} />,
  },
}
